import React, { useEffect, useState, useRef } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Modal, Form, Alert, Badge, Image, Spinner, InputGroup
} from 'react-bootstrap';
import {
  AdminClient,
  DealerDto,
  CreateDealerDto
} from '../web-api-client.ts';

const currencyFormat = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
    : '';

const DealersAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [dealers, setDealers] = useState([]);
  const [error, setError] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [createErr, setCreateErr] = useState(null);

  const [editingDealer, setEditingDealer] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [editErr, setEditErr] = useState(null);

  const [uploadingFor, setUploadingFor] = useState(null); // dealerId currently uploading
  const [deletingLogoFor, setDeletingLogoFor] = useState(null); // dealerId currently deleting

  // --- Load dealers ---
  const loadDealers = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = new AdminClient();
      const list = await client.getDealers();
      setDealers(list || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load dealers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDealers();
  }, []);

  // ====== Create Modal State ======
  const [createForm, setCreateForm] = useState({
    name: '',
    price: null,
    userName: '',
    email: '',
    password: '',
    enforceBilling: false
  });

  const handleCreateChange = (e) => {
  const { name, type, value, checked } = e.target;
  setCreateForm((p) => ({
    ...p,
    [name]:
      type === 'checkbox'
        ? checked
        : name === 'price'
          ? (value === '' ? null : parseFloat(value))
          : value
  }));
};

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateErr(null);
    setCreateMsg(null);
    try {
      const client = new AdminClient();
      const dto = new CreateDealerDto({
        name: createForm.name,
        // Logo now handled by upload endpoint after creation
        logoUrl: null,
        price: createForm.price != null ? parseFloat(createForm.price) : null,
        userName: createForm.userName,
        email: createForm.email,
        password: createForm.password,
        enforceBilling: createForm.enforceBilling
      });

      const result = await client.createDealer(dto);
      if (result?.isSuccessful) {
        setCreateMsg(result.message || 'Dealer created.');
        setCreateForm({
          name: '',
          price: null,
          userName: '',
          email: '',
          password: '',
          enforceBilling: false
        });
        await loadDealers();
        // Optional: Auto-open edit on the newest dealer by name match
        // const created = (await client.getDealers()).find(d => d.name === dto.name);
        // if (created) openEdit(created);
        setShowCreate(false);
      } else {
        setCreateErr(result?.message || 'Failed to create dealer.');
      }
    } catch (err) {
      console.error(err);
      setCreateErr('Error creating dealer.');
    } finally {
      setCreating(false);

    }
  };

  // ====== Edit Modal State ======
  const [editForm, setEditForm] = useState({
    id: null,
    name: '',
    price: null,
    // Logo is managed via upload/delete endpoints; we show preview, not an editable URL
    logoUri: '',
    logoBlobName: '',
    enforceBilling: false
  });

  const openEdit = (dealer) => {
    setEditingDealer(dealer);
    setEditForm({
      id: dealer.id,
      name: dealer.name || '',
      price: dealer.price ?? null,
      logoUri: dealer.logoUri || '',
      logoBlobName: dealer.logoBlobName || '',
      enforceBilling: dealer.enforceBilling ?? false
    });
    setEditErr(null);
    setEditMsg(null);
    setShowEdit(true);
  };

const handleEditChange = (e) => {
  const { name, type, value, checked } = e.target;
  setEditForm((p) => ({
    ...p,
    [name]:
      type === 'checkbox'
        ? checked
        : name === 'price'
          ? (value === '' ? null : parseFloat(value))
          : value
  }));
};
  const submitEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    setEditErr(null);
    setEditMsg(null);
    try {
      const client = new AdminClient();
      const dto = new DealerDto({
        id: editForm.id,
        name: editForm.name,
        // do NOT send logoUri/blobName from here normally—upload endpoint owns these.
        logoUri: editForm.logoUri || null,
        logoBlobName: editForm.logoBlobName || null,
        price: editForm.price != null ? parseFloat(editForm.price) : null,
        enforceBilling: editForm.enforceBilling
      });

      await client.updateDealer(editForm.id, dto);
      setEditMsg('Dealer updated successfully.');
      await loadDealers();

      // Refresh local editForm preview with latest dealer values
      const updated = (dealers || []).find(d => d.id === editForm.id);
      if (updated) {
        setEditForm((p) => ({
          ...p,
          logoUri: updated.logoUri || p.logoUri,
          logoBlobName: updated.logoBlobName || p.logoBlobName,
          enforceBilling: updated.enforceBilling ?? p.enforceBilling
        }));
      }
    } catch (err) {
      console.error(err);
      setEditErr('Failed to update dealer.');
    } finally {
      setSavingEdit(false);
    }
  };

  const totalUsers = (dealer) => dealer?.users?.length ?? 0;

  // ====== Logo Upload/Delete ======
  const fileInputsRef = useRef({}); // store refs per dealerId for row quick-upload

  const getFileInputRef = (dealerId) => {
    if (!fileInputsRef.current[dealerId]) {
      fileInputsRef.current[dealerId] = React.createRef();
    }
    return fileInputsRef.current[dealerId];
  };

  const uploadDealerLogo = async (dealerId, file) => {
    if (!file) return;
    setUploadingFor(dealerId);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/admin/dealers/${dealerId}/logo`, {
        method: 'POST',
        body: form
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Upload failed');
      }
      // Could read returned dto if needed, but we’ll just refresh list
      await loadDealers();

      // If we’re editing the same dealer, update the preview
      if (editingDealer?.id === dealerId) {
        const fresh = (await new AdminClient().getDealers()).find(d => d.id === dealerId);
        if (fresh) {
          setEditForm((p) => ({
            ...p,
            logoUri: fresh.logoUri || '',
            logoBlobName: fresh.logoBlobName || ''
          }));
          setEditMsg('Logo uploaded.');
        }
      }
    } catch (err) {
      console.error(err);
      if (editingDealer?.id === dealerId) {
        setEditErr('Failed to upload logo.');
      } else {
        setError('Failed to upload logo.');
      }
    } finally {
      setUploadingFor(null);
      // clear the input so the same file can be re-selected
      const ref = getFileInputRef(dealerId);
      if (ref?.current) ref.current.value = '';
    }
  };

  const deleteDealerLogo = async (dealerId) => {
    if (!window.confirm('Remove the current logo?')) return;
    setDeletingLogoFor(dealerId);
    try {
      const res = await fetch(`/api/admin/dealers/${dealerId}/logo`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Delete failed');
      }
      await loadDealers();

      if (editingDealer?.id === dealerId) {
        setEditForm((p) => ({ ...p, logoUri: '', logoBlobName: '' }));
        setEditMsg('Logo deleted.');
      }
    } catch (err) {
      console.error(err);
      if (editingDealer?.id === dealerId) {
        setEditErr('Failed to delete logo.');
      } else {
        setError('Failed to delete logo.');
      }
    } finally {
      setDeletingLogoFor(null);
    }
  };

  return (
    <Container className="mt-4">
      <Row className="align-items-center mb-3">
        <Col><h3>Dealers</h3></Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ Create Dealer</Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading dealers...
            </div>
          ) : dealers.length === 0 ? (
            <div className="text-muted">No dealers found.</div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 64 }}></th>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Enforce Billing</th>
                    <th>Users</th>
                    <th style={{ width: 220 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dealers.map(d => (
                    <tr key={d.id}>
                      <td>
                        {d.logoUri ? (
                          <Image src={d.logoUri} alt={d.name} rounded style={{ width: 48, height: 48, objectFit: 'contain', background: 'var(--bs-body-bg)' }} />
                        ) : (
                          <div style={{ width: 48, height: 48 }} className="d-flex align-items-center justify-content-center text-muted border rounded">
                            N/A
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{d.name}</div>
                        {d.logoBlobName && <div className="small text-muted">Blob: {d.logoBlobName}</div>}
                      </td>
                      <td>{currencyFormat(d.price)}</td>
                      <td>{d.enforceBilling ? 'Yes' : 'No'}</td>
                      <td>
                        <Badge bg={totalUsers(d) > 0 ? 'secondary' : 'light'}>{totalUsers(d)}</Badge>
                      </td>
                      <td className="text-end">
                        {/* Hidden input to trigger quick upload */}
                        <input
                          ref={getFileInputRef(d.id)}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => uploadDealerLogo(d.id, e.target.files?.[0])}
                        />
                       
                       
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(d)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ===== Create Dealer Modal ===== */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Dealer</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitCreate}>
          <Modal.Body>
            {createMsg && <Alert variant="success">{createMsg}</Alert>}
            {createErr && <Alert variant="danger">{createErr}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Dealer Name</Form.Label>
              <Form.Control
                name="name"
                value={createForm.name}
                onChange={handleCreateChange}
                required
                placeholder="Test Dealer"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="price"
                  value={createForm.price ?? ''}
                  onChange={handleCreateChange}
                  placeholder="0.00"
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enforceBilling"
                label="Enforce Billing"
                checked={createForm.enforceBilling}
                onChange={handleCreateChange}
              />
            </Form.Group>

            <hr />
            <div className="fw-semibold mb-2">Primary User</div>

            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                name="userName"
                value={createForm.userName}
                onChange={handleCreateChange}
                required
                placeholder="dealeradmin"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={createForm.email}
                onChange={handleCreateChange}
                required
                placeholder="admin@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={createForm.password}
                onChange={handleCreateChange}
                required
              />
            </Form.Group>

            <div className="text-muted small">
              After creating, you can upload a logo from the table or the Edit modal.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={creating}>Close</Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create Dealer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ===== Edit Dealer Modal ===== */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Dealer</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitEdit}>
          <Modal.Body>
            {editMsg && <Alert variant="success">{editMsg}</Alert>}
            {editErr && <Alert variant="danger">{editErr}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Dealer Name</Form.Label>
              <Form.Control
                name="name"
                value={editForm.name}
                onChange={handleEditChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="price"
                  value={editForm.price ?? ''}
                  onChange={handleEditChange}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enforceBilling"
                label="Enforce Billing"
                checked={editForm.enforceBilling}
                onChange={handleEditChange}
              />
            </Form.Group>

            <hr />
            <div className="fw-semibold mb-2">Logo</div>
            {editForm.logoUri ? (
              <div className="mb-2">
                <Image src={editForm.logoUri} alt="Logo" rounded style={{ maxHeight: 80, background: 'var(--bs-body-bg)' }} />
              </div>
            ) : (
              <div className="text-muted mb-2">No logo</div>
            )}

            <Form.Group className="mb-2">
              <Form.Label>Replace Logo</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && editForm.id) {
                    uploadDealerLogo(editForm.id, file);
                  }
                }}
                disabled={uploadingFor === editForm.id}
              />
              <div className="d-flex gap-2 mt-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteDealerLogo(editForm.id)}
                  disabled={deletingLogoFor === editForm.id}
                >
                  {deletingLogoFor === editForm.id ? 'Deleting…' : 'Remove Logo'}
                </Button>
              </div>
            </Form.Group>

            {editForm.logoBlobName && (
              <div className="small text-muted">Blob: {editForm.logoBlobName}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEdit(false)} disabled={savingEdit}>Close</Button>
            <Button type="submit" variant="primary" disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default DealersAdmin;
